import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getConfig } from '@edx/frontend-platform';
import { getLocale, useIntl } from '@edx/frontend-platform/i18n';
import { getAuthenticatedHttpClient } from '@edx/frontend-platform/auth';

import { preferencesApi } from '../../../api';
import { useHeaderFooter } from '../../../hooks';
import { AppDataContext } from '../../../context';
import { SpinLoader } from '../../../components';
import {
  ControllerFieldInput,
  ControllerFiledPassword,
  ControllerFieldCheckbox,
  FooterLinks,
} from '../components';
import { getRedirectToUrl } from '../utils';
import RegistrationNotification from './RegistrationNotification';
import { getSelectedLanguage, isFormValid } from './utils';
import {
  RegistrationProps,
  FieldErrorsTypes,
  FieldTypes,
  ErrorsTypes,
} from './types';
import {
  FORM_SUBMISSION_ERROR,
  REGISTER_INITIAL_CODE_STATE,
  REGISTER_INITIAL_FORM_STATE,
} from './constants';

import logistrationMessages from '../messages';
import messages from './messages';

const Registration = ({ children }: RegistrationProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formFields, setFormFields] = useState<FieldTypes>(REGISTER_INITIAL_FORM_STATE);
  const [notificationCode, setNotificationCode] = useState<ErrorsTypes>(REGISTER_INITIAL_CODE_STATE);
  const [errors, setErrors] = useState<FieldErrorsTypes>({});

  const intl = useIntl();
  const { authenticatedUser } = useContext(AppDataContext);
  const { headerContent } = useHeaderFooter(children);
  const redirectToUrl = getRedirectToUrl();
  const selectedLanguage = getSelectedLanguage(getLocale(), getConfig().SITE_LANGUAGES || []);

  useEffect(() => {
    if (authenticatedUser) {
      window.location.replace(redirectToUrl);
    }
  }, [authenticatedUser]);

  if (authenticatedUser) {
    return null;
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = event.target;
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
    setFormFields(prevState => ({ ...prevState, [name]: value }));
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name } = event.target;
    setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
  };

  const registerRequest = async (formData: FormData) => {
    const requestConfig = {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      isPublic: true,
    };

    try {
      setIsSubmitting(true);
      const { data } = await getAuthenticatedHttpClient()
        .post(
          `${getConfig().LMS_BASE_URL}/api/user/v2/account/registration/`,
          formData,
          requestConfig,
        )
        .catch((e: unknown) => {
          throw (e);
        });

      return {
        redirectUrl: data?.redirect_url || '',
        success: data?.success || false,
        authenticatedUser: data?.authenticated_user,
      };
    } catch (error) {
      if (error) {
        const {
          error_code: code,
          context,
          ...registrationError
        } = JSON.parse((error as any).customAttributes.httpErrorResponseData) ?? {};
        setNotificationCode(prevState => ({ type: code, count: prevState.count + 1, context }));
        Object.keys(registrationError || {}).forEach((field: string) => {
          setErrors(prevErrors => ({
            ...prevErrors,
            [field]: registrationError[field][0].user_message || '',
          }));
        });
      }
      setIsSubmitting(false);
      return null;
    }
  };

  const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    // Validating form data before submitting
    const { isValid, fieldErrors } = isFormValid(formFields, intl);
    setErrors(fieldErrors);

    if (!isValid) {
      setNotificationCode(prevState => ({ type: FORM_SUBMISSION_ERROR, count: prevState.count + 1 }));
      return;
    }

    const email = formFields.email.trim();
    const username = formFields.username.trim();

    const formData = new FormData();
    formData.append('email', email);
    formData.append('name', username);
    formData.append('username', username);
    formData.append('password', formFields.password);
    formData.append('honor_code', String(formFields.honor_code));
    formData.append('next', redirectToUrl || window.location.origin);

    const { redirectUrl, success } = await registerRequest(formData) ?? {};
    if (success && redirectUrl) {
      // Update the user's language preference in the backend
      // Uses the current UI locale and the newly created username
      await preferencesApi.updateLanguagePreference(true, getLocale(), formFields.username);

      // Redirect to the previous location
      window.location.replace(redirectToUrl);
    }
  };

  const honorCodeLink = (chunks: React.ReactNode) => (
    <Link to="/privacy-policy" target="_blank">{chunks}</Link>
  );

  const contactUsLink = (chunks: React.ReactNode) => (
    <Link to="/contact" target="_blank">{chunks}</Link>
  );

  const boldWrapper = (chunks: React.ReactNode) => (
    <strong>{chunks}</strong>
  );

  return (
    <div className="container-center-horizontal">
      <div className="logistration-main screen">
        {headerContent}
        <div className="logistration">
          <div className="auth-page-block">
            <div className="auth-page-main-content">
              <RegistrationNotification
                code={notificationCode.type}
                count={notificationCode.count}
              />
              <h2 className="auth-page-title">
                {intl.formatMessage(messages.title)}
              </h2>
              <div className="auth-title-desc">
                {intl.formatMessage(messages.description)}
              </div>
              <form className="auth-form">
                <ControllerFieldInput
                  name="email"
                  placeholder={intl.formatMessage(messages.emailFieldPlaceholder)}
                  value={formFields.email}
                  handleChange={handleChange}
                  handleFocus={handleFocus}
                  error={errors.email}
                  helpText={intl.formatMessage(messages.emailFieldHelpText)}
                />
                <ControllerFieldInput
                  name="username"
                  placeholder={intl.formatMessage(messages.usernameFieldPlaceholder)}
                  value={formFields.username}
                  handleChange={handleChange}
                  handleFocus={handleFocus}
                  error={errors.username}
                  helpText={intl.formatMessage(messages.usernameFieldHelpText)}
                />
                <ControllerFiledPassword
                  name="password"
                  placeholder={intl.formatMessage(messages.passwordFieldPlaceholder)}
                  value={formFields.password}
                  handleChange={handleChange}
                  handleFocus={handleFocus}
                  error={errors.password}
                />
                <ControllerFiledPassword
                  name="confirm_password"
                  placeholder={intl.formatMessage(messages.confirmPasswordFieldPlaceholder)}
                  value={formFields.confirm_password}
                  handleChange={handleChange}
                  handleFocus={handleFocus}
                  error={errors.confirm_password}
                />
                {!!getConfig()?.SITE_LANGUAGES?.length && (
                  <div className="auth-alert">
                    {intl.formatMessage(messages.currentLanguageMessage, {
                      language: selectedLanguage,
                      bold: boldWrapper,
                      link: contactUsLink,
                    })}
                  </div>
                )}
                <button
                  className="auth-submit-btn"
                  type="submit"
                  aria-label={intl.formatMessage(logistrationMessages.submitButtonText)}
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      {intl.formatMessage(logistrationMessages.submitLoadingButtonText)}
                      <SpinLoader size={20} color="#fff" />
                    </>
                  ) : intl.formatMessage(logistrationMessages.submitButtonText)}
                </button>
                <ControllerFieldCheckbox
                  name="honor_code"
                  label={intl.formatMessage(messages.honorCodeCheckboxLabel, {
                    link: honorCodeLink,
                  })}
                  handleChange={handleChange}
                />
              </form>
              <div className="auth-redirect-block">
                {intl.formatMessage(messages.hasAccount)}
                {' '}
                <Link
                  className="auth-link-redirect"
                  to={`/login?redirect-to=${redirectToUrl}`}
                >
                  {intl.formatMessage(messages.loginLinkText)}
                </Link>
              </div>
            </div>
            <FooterLinks />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Registration;
